/**
 * <kj-product-popup delay="10" days="3" hidden> shows a small product card after `delay` seconds
 * or when the shopper has scrolled halfway down. Never shows while a dialog is open; waits and retries.
 * Closing hides it for `days` days. Escape closes it.
 */
class KjProductPopup extends HTMLElement {
  static KEY = 'kj-product-popup-closed';
  #timer = 0;
  #retry = 0;

  #onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max > 0 && window.scrollY / max >= 0.5) this.#show();
  };

  #onKey = (event) => {
    if (event.key === 'Escape' && !this.hidden) this.#close();
  };

  connectedCallback() {
    if (this.#recentlyClosed()) return;
    this.querySelector('[data-close]')?.addEventListener('click', () => this.#close());
    const delay = Number(this.getAttribute('delay') || 10) * 1000;
    this.#timer = window.setTimeout(() => this.#show(), delay);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.#cleanup();
    document.removeEventListener('keydown', this.#onKey);
  }

  #cleanup() {
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#retry);
    window.removeEventListener('scroll', this.#onScroll);
  }

  #show() {
    if (!this.hidden) return;
    if (document.querySelector('dialog[open]')) {
      window.clearTimeout(this.#retry);
      this.#retry = window.setTimeout(() => this.#show(), 4000);
      return;
    }
    this.#cleanup();
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-visible'));
    document.addEventListener('keydown', this.#onKey);
  }

  #close() {
    this.classList.remove('is-visible');
    document.removeEventListener('keydown', this.#onKey);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(() => { this.hidden = true; }, reduce ? 0 : 300);
    try { localStorage.setItem(KjProductPopup.KEY, String(Date.now())); } catch {}
  }

  #recentlyClosed() {
    try {
      const at = Number(localStorage.getItem(KjProductPopup.KEY) || 0);
      const days = Number(this.getAttribute('days') || 3);
      return Date.now() - at < days * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('kj-product-popup')) customElements.define('kj-product-popup', KjProductPopup);
